import { useState, useEffect, useMemo, useRef, useCallback, type CSSProperties } from 'react';
import { useApp, Icon, Pill, Banner, Input } from '@/components/ds';
import { Method, Code, CodeBlock, Spinner, METHOD_COLORS, CODE_THEME } from '@/components/code';
import { genAll, jsonStr, type Endpoint } from '@/lib/codegen';
import { GROUPS } from '@/data';
import { request } from '@/api/client';

// reference.tsx — three-pane API reference with live multi-language samples + try-it console.

// ---- Data shapes (loosely typed for dynamic spec objects) ----------------
interface ParamRowData {
  name: string;
  type?: string;
  required?: boolean;
  trusted?: boolean;
  desc?: string;
  options?: string[];
  shortName?: string;
}

type ParamRowInput = ParamRowData | [string, string, string];

interface EndpointSpec {
  path?: string;
  body?: Record<string, unknown>;
  [key: string]: unknown;
}

interface EndpointData {
  id: string;
  name: string;
  method: string;
  path: string;
  summary?: string;
  auth?: string;
  badge?: string;
  params?: ParamRowInput[];
  queryParams?: ParamRowInput[];
  headerParams?: ParamRowInput[];
  fields?: ParamRowInput[];
  responseFields?: ParamRowInput[];
  spec?: EndpointSpec;
  examples?: { label: string; body: Record<string, unknown> }[];
  response?: unknown;
  errorResponse?: unknown;
}

interface TryItResult {
  status: number;
  ms: number;
  reqId: string;
  body: unknown;
  live: boolean;
}

// ---- Left nav -----------------------------------------------------------
interface RefNavProps {
  selected: string;
  onSelect: (id: string) => void;
}

function ThemeToggle() {
  const app = useApp();
  const dark = app.codeDark;
  return (
    <button
      role="switch"
      aria-checked={dark}
      onClick={() => app.setCodeDark(!dark)}
      aria-label={dark ? 'Switch to light code theme' : 'Switch to dark code theme'}
      title={dark ? 'Dark theme' : 'Light theme'}
      style={{
        width: 62,
        height: 38,
        flexShrink: 0,
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        padding: 5,
        border: '1px solid var(--border-static)',
        borderRadius: 10,
        background: 'var(--neutral-200)',
        cursor: 'pointer',
        transition: 'background-color .15s',
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          background: 'var(--neutral-100)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'var(--shadow-400)',
          transform: dark ? 'translateX(24px)' : 'translateX(0)',
          transition: 'transform .15s',
          color: dark ? 'var(--icon-primary)' : 'var(--warning-700)',
        }}
      >
        {dark ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 8.5A6 6 0 1 1 7.5 2 4.7 4.7 0 0 0 14 8.5Z" />
          </svg>
        ) : (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="8" cy="8" r="3.2" />
            <path d="M8 1v1.5M8 13.5V15M15 8h-1.5M2.5 8H1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1M12.9 12.9l-1.1-1.1M4.2 4.2 3.1 3.1" />
          </svg>
        )}
      </span>
    </button>
  );
}

function RefNav({ selected, onSelect }: RefNavProps) {
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ 'getting-started': true });

  const activeGroupId = useMemo(() => {
    for (const g of GROUPS as any[]) {
      if ((g.pages || g.endpoints).some((item: { id: string }) => selected === (g.pages ? 'guide:' : 'ep:') + item.id)) return g.id;
    }
    return undefined;
  }, [selected]);

  // Auto-expand whichever group contains the currently active (scrolled-to) section.
  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((prev) => (prev[activeGroupId] ? prev : { ...prev, [activeGroupId]: true }));
  }, [activeGroupId]);

  const q = query.trim().toLowerCase();
  const toggleGroup = (id: string) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <nav
      style={{
        width: 256,
        flexShrink: 0,
        position: 'sticky',
        top: 64,
        alignSelf: 'flex-start',
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--border-static)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px', minWidth: 0 }}>
        <Input
          value={query}
          onChange={setQuery}
          placeholder="Search"
          prefix={<Icon name="search" size={15} color="var(--icon-primary)" />}
          suffix={
            <span className="ref-body-sm" style={{ color: 'var(--text-secondary)' }}>
              ⌘K
            </span>
          }
          fontSize={14}
          style={{
            flex: '1 1 0%',
            minWidth: 0,
            height: 38,
            border: '1px solid var(--border-static)',
            borderRadius: 10,
            fontFamily: 'var(--font-linear-sans)',
            fontWeight: 300,
          }}
        />
        <ThemeToggle />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px 16px' }}>
        {(GROUPS as any[]).map((g) => {
          const items = (g.pages || g.endpoints) as { id: string; name: string; method?: string }[];
          const filtered = q ? items.filter((item) => item.name.toLowerCase().includes(q)) : items;
          if (q && filtered.length === 0) return null;
          const isOpen = q ? true : !!openGroups[g.id];
          return (
            <div key={g.id} style={{ marginBottom: 4 }}>
              <button
                onClick={() => toggleGroup(g.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  gap: 8,
                  height: 40,
                  padding: '0 10px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={15} color="var(--icon-primary)" />
                <span className="ref-body-strong">{g.label}</span>
              </button>
              {isOpen && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    marginLeft: 19,
                    paddingLeft: 15,
                    borderLeft: '1px solid var(--border-static)',
                  }}
                >
                  {filtered.map((item) => {
                    const id = (g.pages ? 'guide:' : 'ep:') + item.id;
                    const active = selected === id;
                    const methodColor = item.method ? METHOD_COLORS[item.method]?.[1] : undefined;
                    return (
                      <button
                        key={id}
                        onClick={() => onSelect(id)}
                        title={item.name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          minWidth: 0,
                          gap: 8,
                          height: 38,
                          padding: '0 10px',
                          borderRadius: 6,
                          border: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          background: active ? 'var(--brand-100)' : 'transparent',
                          color: active ? 'var(--accent-text)' : 'var(--text-secondary)',
                          transition: 'background-color .12s, color .12s',
                        }}
                      >
                        <span
                          className={active ? 'ref-body-strong' : 'ref-body'}
                          style={{ color: 'inherit', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {item.name}
                        </span>
                        {item.method && (
                          <span className="om-mono" style={{ fontSize: 11.5, fontWeight: 700, color: methodColor, flexShrink: 0 }}>
                            {item.method}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ padding: 12, borderTop: '1px solid var(--border-static)' }}>
        <a
          href="/dashboard"
          className="ref-body-strong"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 'var(--ref-control-height)',
            padding: '0 10px',
            borderRadius: 7,
            border: '1px solid var(--border-static)',
            textDecoration: 'none',
            color: 'var(--text-primary)',
          }}
        >
          <Icon name="arrow-right" size={14} color="var(--icon-primary)" style={{ transform: 'rotate(-45deg)' }} />
          Open API Client
        </a>
      </div>
    </nav>
  );
}

// ---- Path util for editable fields --------------------------------------
function getAtPath(obj: Record<string, unknown> | null | undefined, path: string): unknown {
  if (!obj || !path) return undefined;
  return path
    .split('.')
    .reduce<unknown>((o, k) => (o != null && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), obj);
}

function setAtPath(obj: Record<string, unknown> | undefined | null, path: string, value: unknown): Record<string, unknown> {
  const parts = path.split('.');
  if (parts.length === 1) return { ...(obj ?? {}), [path]: value };
  const [head, ...rest] = parts;
  return { ...(obj ?? {}), [head]: setAtPath((obj?.[head] as Record<string, unknown>) ?? {}, rest.join('.'), value) };
}

// Extract {paramName} values from a concrete path by matching against the template.
// e.g. template '/v2/entities/{entityId}', concrete '/v2/entities/ent_9688713'
// → { businessId: 'org_9688713' }
function extractPathParamValues(templatePath: string, concretePath?: string): Record<string, string> {
  const tParts = templatePath.split('/');
  const cParts = (concretePath || templatePath).split('/');
  const values: Record<string, string> = {};
  tParts.forEach((part, i) => {
    const m = part.match(/^\{(\w+)\}$/);
    if (m) values[m[1]] = cParts[i] || '';
  });
  return values;
}

// Reconstruct a concrete path by substituting {paramName} placeholders.
function buildConcretePath(templatePath: string, paramValues: Record<string, string>): string {
  return templatePath.replace(/\{(\w+)\}/g, (_, name: string) => paramValues[name] || `{${name}}`);
}

// ---- Schema tables ------------------------------------------------------

interface GroupedLeaf {
  kind: 'leaf';
  row: ParamRowData;
}

interface GroupedGroup {
  kind: 'group';
  prefix: string;
  children: ParamRowData[];
}

type GroupedItem = GroupedLeaf | GroupedGroup;

// Group flat dot-notation rows into leaf rows and nested groups.
// e.g. [{name:'taxInfo.type'}, {name:'taxInfo.identifier'}] →
//      [{kind:'group', prefix:'taxInfo', children:[...]}]
function groupRows(rows: ParamRowData[]): GroupedItem[] {
  const result: GroupedItem[] = [];
  const groupMap = new Map<string, GroupedGroup>();
  for (const row of rows) {
    const dot = row.name.indexOf('.');
    if (dot === -1) {
      result.push({ kind: 'leaf', row });
    } else {
      const prefix = row.name.slice(0, dot);
      const shortName = row.name.slice(dot + 1);
      if (!groupMap.has(prefix)) {
        const g: GroupedGroup = { kind: 'group', prefix, children: [] };
        result.push(g);
        groupMap.set(prefix, g);
      }
      groupMap.get(prefix)!.children.push({ ...row, shortName });
    }
  }
  return result;
}

interface ParamRowProps {
  r: ParamRowData;
  displayName: string;
  body?: Record<string, unknown> | null;
  onFieldChange?: (name: string, value: unknown) => void;
  hasBorderTop: boolean;
  indent: boolean;
}

function ParamRow({ r, displayName, body, onFieldChange, hasBorderTop, indent }: ParamRowProps) {
  const currentVal = body ? getAtPath(body, r.name) : undefined;
  // Show input for any field that isn't an object/array type — even if absent from the body draft
  const typeIsComplex = r.type === 'object' || r.type === 'array' || (typeof r.type === 'string' && r.type.includes('[]'));
  const isEditable = !typeIsComplex && !!onFieldChange;
  const displayVal = currentVal !== undefined ? String(currentVal) : '';
  return (
    <div
      style={{
        padding: '11px 16px',
        paddingLeft: indent ? 32 : 16,
        borderTop: hasBorderTop ? '1px solid var(--border-static)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        background: indent ? 'var(--neutral-200)' : 'transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <code className="om-mono" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
          {displayName}
        </code>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>{r.type}</span>
        {r.required && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 8px',
              borderRadius: 'var(--radius-100)',
              background: 'var(--neutral-300)',
              fontSize: 10,
              lineHeight: '16px',
              fontWeight: 400,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
            }}
          >
            required
          </span>
        )}
        {r.trusted && (
          <Pill kind="brand" dot={false} style={{ fontSize: 10.5, height: 18, padding: '0 7px' }}>
            Trusted
          </Pill>
        )}
        {isEditable && r.options && r.options.length > 0 ? (
          <select
            value={displayVal}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const val = e.target.value;
              const coerced = r.type === 'boolean' ? (val === 'true' ? true : val === 'false' ? false : val) : val;
              onFieldChange!(r.name, coerced);
            }}
            style={{
              marginLeft: 'auto',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              padding: '3px 8px',
              border: '1px solid var(--border-static)',
              borderRadius: 6,
              background: indent ? 'var(--neutral-100)' : 'var(--neutral-200)',
              color: 'var(--text-primary)',
              outline: 'none',
              minWidth: 120,
              maxWidth: 220,
              cursor: 'pointer',
            }}
          >
            {!displayVal && <option value="">— select —</option>}
            {r.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : isEditable ? (
          <input
            value={displayVal}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              const coerced =
                r.type === 'integer' || r.type === 'number' ? (raw === '' ? '' : isNaN(Number(raw)) ? raw : Number(raw)) : raw;
              onFieldChange!(r.name, coerced);
            }}
            spellCheck={false}
            style={{
              marginLeft: 'auto',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              padding: '3px 8px',
              border: '1px solid var(--border-static)',
              borderRadius: 6,
              background: indent ? 'var(--neutral-100)' : 'var(--neutral-200)',
              color: 'var(--text-primary)',
              outline: 'none',
              minWidth: 120,
              maxWidth: 220,
            }}
          />
        ) : null}
      </div>
      {r.desc && <div className="ref-body-sm">{r.desc}</div>}
    </div>
  );
}

interface ParamTableProps {
  title?: string;
  rows?: ParamRowInput[];
  body?: Record<string, unknown> | null;
  onFieldChange?: (name: string, value: unknown) => void;
}

function ParamTable({ title, rows, body, onFieldChange }: ParamTableProps) {
  // Compute groups before useState so the initializer can open all of them
  const normalizedRows: ParamRowData[] = (rows || []).map((r) =>
    Array.isArray(r) ? { name: r[0], type: r[1], required: false, desc: r[2] } : r
  );
  const grouped = groupRows(normalizedRows);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    grouped.forEach((item) => {
      if (item.kind === 'group') init[item.prefix] = true;
    });
    return init;
  });

  if (!rows || !rows.length) return null;

  const toggle = (prefix: string) => setOpenGroups((prev) => ({ ...prev, [prefix]: !prev[prefix] }));

  let rowIndex = 0;
  return (
    <div style={{ marginTop: 28 }}>
      <div className="ref-body-sm-strong" style={{ marginBottom: 10 }}>
        {title}
      </div>
      <div style={{ border: '1px solid var(--border-static)', borderRadius: 10, overflow: 'hidden' }}>
        {grouped.map((item) => {
          if (item.kind === 'leaf') {
            const el = (
              <ParamRow
                key={item.row.name}
                r={item.row}
                displayName={item.row.name}
                body={body}
                onFieldChange={onFieldChange}
                hasBorderTop={rowIndex > 0}
                indent={false}
              />
            );
            rowIndex++;
            return el;
          }
          // Group header + collapsible children
          const isOpen = !!openGroups[item.prefix];
          const hasBorder = rowIndex > 0;
          rowIndex++;
          return (
            <div key={item.prefix}>
              <button
                onClick={() => toggle(item.prefix)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '11px 16px',
                  background: 'none',
                  border: 'none',
                  borderTop: hasBorder ? '1px solid var(--border-static)' : 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <code className="om-mono" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {item.prefix}
                </code>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>object</span>
                <Icon
                  name={isOpen ? 'chevron-up' : 'chevron-down'}
                  size={14}
                  color="var(--text-secondary)"
                  style={{ marginLeft: 'auto' }}
                />
              </button>
              {isOpen &&
                item.children.map((child) => (
                  <ParamRow
                    key={child.name}
                    r={child}
                    displayName={child.shortName ?? child.name}
                    body={body}
                    onFieldChange={onFieldChange}
                    hasBorderTop={true}
                    indent={true}
                  />
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SELECT_DARK: CSSProperties = {
  background: CODE_THEME.dark.panel,
  color: CODE_THEME.dark.tabText,
  border: `1px solid ${CODE_THEME.dark.border}`,
  borderRadius: 6,
  fontSize: 11.5,
  padding: '4px 8px',
  cursor: 'pointer',
};

// ---- Try-it console -----------------------------------------------------
// result/setResult are lifted to EndpointDoc so they persist across navigation.
// onLiveRequest is called when a real HTTP request starts (collapses the example).
interface TryItProps {
  ep: EndpointData;
  bodyDraft?: Record<string, unknown> | null;
  setBodyDraft: (val: Record<string, unknown> | null | ((prev: Record<string, unknown> | null) => Record<string, unknown> | null)) => void;
  concretePath?: string;
  result?: TryItResult | null;
  setResult: (val: TryItResult | null | ((prev: TryItResult | null) => TryItResult | null)) => void;
  onLiveRequest?: () => void;
}

function TryIt({ ep, bodyDraft, setBodyDraft, concretePath, result, setResult, onLiveRequest }: TryItProps) {
  const app = useApp();
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState('success');
  const [payloadStr, setPayloadStr] = useState(() => (bodyDraft ? jsonStr(bodyDraft) : ''));
  const [payloadError, setPayloadError] = useState<string | null>(null);

  const canFail = !!ep.errorResponse;
  const hasBody = !!bodyDraft;
  // Empty-object responses model a 204 (deletes, sandbox actions) regardless of HTTP method.
  const isEmptyResponse = !!ep.response && typeof ep.response === 'object' && Object.keys(ep.response as object).length === 0;
  const successStatus = isEmptyResponse ? 204 : ep.method === 'POST' ? 201 : 200;

  // Sync textarea from external bodyDraft changes (field edits)
  const prevDraftRef = useRef(bodyDraft);
  useEffect(() => {
    if (prevDraftRef.current !== bodyDraft) {
      prevDraftRef.current = bodyDraft;
      if (bodyDraft) setPayloadStr(jsonStr(bodyDraft));
    }
  }, [bodyDraft]);

  const run = async () => {
    setRunning(true);
    setResult(null);
    setPayloadError(null);

    if (app.hasKey) {
      onLiveRequest?.();
      let body: Record<string, unknown> | undefined;
      if (hasBody) {
        try {
          body = JSON.parse(payloadStr) as Record<string, unknown>;
        } catch {
          setPayloadError('Invalid JSON — fix before sending');
          setRunning(false);
          return;
        }
      }
      const t0 = Date.now();
      try {
        // apiBaseUrl already includes /v2; strip it from the path to avoid duplication
        const apiPath = (concretePath || ep.spec?.path || ep.path).replace(/^\/v2/, '');
        const data = await request({ env: app.env, apiKey: app.apiKey }, apiPath, {
          method: ep.method as 'GET' | 'POST' | 'PATCH' | 'DELETE',
          body,
        });
        setResult({
          status: successStatus,
          ms: Date.now() - t0,
          reqId: 'req_' + Math.random().toString(36).slice(2, 8),
          // A 204 response has no body — request() resolves `undefined`; show `{}` rather than the literal text "undefined".
          body: data ?? {},
          live: true,
        });
      } catch (error: unknown) {
        const err = error as { status?: number; body?: unknown; message?: string };
        setResult({
          status: err.status || 500,
          ms: Date.now() - t0,
          reqId: 'req_' + Math.random().toString(36).slice(2, 8),
          body: err.body || { error: { message: err.message } },
          live: true,
        });
      }
      setRunning(false);
    } else {
      // Mock: simulate latency
      const ms = 280 + Math.floor(Math.random() * 360);
      setTimeout(() => {
        setRunning(false);
        const fail = canFail && outcome === 'ineligible';
        setResult({
          status: fail ? 409 : successStatus,
          ms,
          reqId: 'req_' + Math.random().toString(36).slice(2, 8),
          body: fail ? ep.errorResponse : ep.response,
          live: false,
        });
      }, ms + 200);
    }
  };

  const statusOk = result && result.status < 400;
  const statusText = result
    ? result.status === 201
      ? 'Created'
      : result.status === 204
        ? 'No Content'
        : result.status === 200
          ? 'OK'
          : result.status === 409
            ? 'Conflict'
            : result.status === 401
              ? 'Unauthorized'
              : result.status === 404
                ? 'Not Found'
                : result.status >= 500
                  ? 'Server Error'
                  : ''
    : '';

  return (
    <div style={{ borderTop: `1px solid ${CODE_THEME.dark.border}` }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          background: CODE_THEME.dark.tabbar,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 700, color: CODE_THEME.dark.text }}>Try it</span>

        {/* Key indicator */}
        {app.hasKey ? (
          <span style={{ fontSize: 11, color: CODE_THEME.dark.st, fontFamily: "'JetBrains Mono', monospace" }}>● key set</span>
        ) : (
          <span style={{ fontSize: 11, color: CODE_THEME.dark.tabText, fontFamily: "'JetBrains Mono', monospace" }}>○ mock (no key)</span>
        )}

        <div style={{ flex: 1 }} />

        {/* Mock outcome picker — only shown when no real key */}
        {canFail && !app.hasKey && (
          <select
            value={outcome}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOutcome(e.target.value)}
            className="om-mono"
            style={SELECT_DARK}
          >
            <option value="success">→ 201 success</option>
            <option value="ineligible">→ 409 not eligible</option>
          </select>
        )}

        <button
          onClick={run}
          disabled={running}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            height: 30,
            padding: '0 14px',
            borderRadius: 7,
            border: 'none',
            cursor: running ? 'default' : 'pointer',
            background: 'var(--accent)',
            color: 'var(--text-inverse)',
            fontSize: 12.5,
            fontWeight: 600,
          }}
        >
          {running ? (
            <Spinner size={13} color="var(--text-inverse)" stroke={2} />
          ) : (
            <Icon name="fast" size={14} color="var(--text-inverse)" />
          )}
          {running ? 'Sending…' : 'Send request'}
        </button>
      </div>

      {/* Payload editor */}
      {hasBody && (
        <div style={{ background: CODE_THEME.dark.panel, borderTop: `1px solid ${CODE_THEME.dark.border}`, padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span
              style={{ fontSize: 10.5, fontWeight: 700, color: CODE_THEME.dark.cm, textTransform: 'uppercase', letterSpacing: '.06em' }}
            >
              Request body
            </span>
            {payloadError && (
              <span style={{ fontSize: 11, color: 'var(--critical-400)', fontFamily: "'JetBrains Mono', monospace" }}>{payloadError}</span>
            )}
          </div>
          <textarea
            value={payloadStr}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const v = e.target.value;
              setPayloadStr(v);
              setPayloadError(null);
              try {
                const parsed = JSON.parse(v) as Record<string, unknown>;
                prevDraftRef.current = parsed;
                setBodyDraft(parsed);
              } catch {}
            }}
            spellCheck={false}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'transparent',
              border: payloadError ? '1px solid var(--critical-400)' : '1px solid transparent',
              borderRadius: 6,
              outline: 'none',
              color: CODE_THEME.dark.text,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12.5,
              lineHeight: '20px',
              resize: 'vertical',
              minHeight: 90,
              padding: '4px 2px',
            }}
          />
        </div>
      )}

      {/* Pending indicator — shown while request is in-flight */}
      {running && (
        <div style={{ borderTop: `1px solid ${CODE_THEME.dark.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', background: CODE_THEME.dark.bg }}>
            <Spinner size={11} color={CODE_THEME.dark.cm} stroke={2} />
            <span className="om-mono" style={{ fontSize: 12, color: CODE_THEME.dark.cm }}>
              {app.hasKey ? 'Waiting for response…' : 'Simulating response…'}
            </span>
          </div>
        </div>
      )}

      {/* Result */}
      {!running && result && (
        <div style={{ animation: 'om-fade .25s ease' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 16px',
              background: CODE_THEME.dark.bg,
              borderBottom: `1px solid ${CODE_THEME.dark.border}`,
            }}
          >
            <span
              className="om-mono"
              style={{ fontSize: 12, fontWeight: 700, color: statusOk ? CODE_THEME.dark.st : 'var(--critical-400)' }}
            >
              ● {result.status} {statusText}
            </span>
            <span className="om-mono" style={{ fontSize: 11.5, color: CODE_THEME.dark.cm }}>
              {result.ms} ms
            </span>
            <span className="om-mono" style={{ fontSize: 11.5, color: CODE_THEME.dark.cm }}>
              {result.reqId}
            </span>
            {result.live && (
              <span style={{ fontSize: 10.5, color: CODE_THEME.dark.st, fontFamily: "'JetBrains Mono', monospace", marginLeft: 4 }}>
                live
              </span>
            )}
          </div>
          <CodeBlock dark code={jsonStr(result.body)} maxHeight={260} />
        </div>
      )}
    </div>
  );
}

// ---- Endpoint doc -------------------------------------------------------
interface EndpointDocProps {
  ep: EndpointData;
}

function EndpointDoc({ ep }: EndpointDocProps) {
  const app = useApp();

  const [bodyDraft, setBodyDraft] = useState<Record<string, unknown> | null>(() => ep.spec?.body ?? null);
  const [pathParams, setPathParams] = useState<Record<string, string>>(() => extractPathParamValues(ep.path, ep.spec?.path));
  const [result, setResult] = useState<TryItResult | null>(null);
  const [exampleCollapsed, setExampleCollapsed] = useState<boolean>(true);

  const handleFieldChange = useCallback(
    (path: string, value: unknown) => {
      setBodyDraft((prev) => setAtPath(prev ?? {}, path, value));
    },
    [setBodyDraft]
  );

  const handlePathParamChange = useCallback(
    (name: string, value: unknown) => {
      setPathParams((prev) => ({ ...prev, [name]: value as string }));
    },
    [setPathParams]
  );

  const handleLiveRequest = useCallback(() => {
    setExampleCollapsed(true);
  }, []);

  const concretePath = buildConcretePath(ep.path, pathParams);

  // Recompute code samples whenever body, path params, apiKey, or env changes
  const editedSpec = useMemo(
    () => ({ ...(bodyDraft != null ? { ...ep.spec, body: bodyDraft } : ep.spec), path: concretePath }),
    [ep.spec, bodyDraft, concretePath]
  );
  const langs = useMemo(() => genAll(editedSpec as Endpoint, app.apiKey, app.apiBase), [editedSpec, app.apiKey, app.apiBase]);

  // Empty-object responses model a 204 (deletes, sandbox actions) regardless of HTTP method.
  const isEmptyResponse = !!ep.response && typeof ep.response === 'object' && Object.keys(ep.response as object).length === 0;
  const responseStatusLabel = isEmptyResponse ? '204 No Content' : ep.method === 'POST' ? '201 Created' : '200 OK';

  return (
    <div className="doc-cols">
      {/* center: doc */}
      <div style={{ minWidth: 0, paddingBottom: 60 }}>
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <h1 className="ref-h1" style={{ margin: 0 }}>
              {ep.name}
            </h1>
            {ep.badge && (
              <Pill kind="brand" dot={false}>
                {ep.badge}
              </Pill>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              background: 'var(--neutral-200)',
              border: '1px solid var(--border-static)',
              borderRadius: 9,
              marginBottom: 20,
            }}
          >
            <Method m={ep.method} />
            <code className="om-mono" style={{ fontSize: 13, color: 'var(--text-primary)', wordBreak: 'break-all' }}>
              {ep.path}
            </code>
          </div>
          <p className="ref-body" style={{ color: 'var(--text-secondary)', margin: 0 }}>
            {ep.summary}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <Icon name="lock" size={15} color="var(--icon-primary)" />
            <span className="ref-body-sm">
              Auth: <strong style={{ color: 'var(--text-primary)' }}>{ep.auth}</strong>
            </span>
          </div>
          <ParamTable title="Path parameters" rows={ep.params} body={pathParams} onFieldChange={handlePathParamChange} />
          {ep.queryParams && <ParamTable title="Query parameters" rows={ep.queryParams} />}
          {ep.headerParams && <ParamTable title="Request headers" rows={ep.headerParams} />}
          <ParamTable title="Body parameters" rows={ep.fields} body={bodyDraft} onFieldChange={handleFieldChange} />
          {ep.examples && ep.examples.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '.04em',
                  marginBottom: 12,
                }}
              >
                Request examples by type
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {ep.examples.map((ex) => (
                  <div key={ex.label}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{ex.label}</div>
                    <CodeBlock dark={app.codeDark} code={jsonStr(ex.body)} maxHeight={280} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {ep.responseFields && <ParamTable title="Response fields" rows={ep.responseFields} />}
          {ep.errorResponse && (
            <div style={{ marginTop: 28 }}>
              <Banner kind="warning">
                <strong>409 Conflict</strong> — <Code>BUSINESS_NOT_ELIGIBLE</Code> when KYC is incomplete (<Code>details</Code> echoes the
                exact <Code>missingFields</Code>), <Code>DUPLICATE_EXTERNAL_ID</Code> when the externalId is already used, or{' '}
                <Code>IDEMPOTENCY_KEY_REUSED</Code> when the Idempotency-Key header was reused with a different body.
              </Banner>
            </div>
          )}
        </>
      </div>
      {/* right: code + try-it (sticky) */}
      <div className="doc-aside" style={{ position: 'sticky', top: 88, minWidth: 0 }}>
        <div
          className="codetheme"
          style={{
            borderRadius: 12,
            overflow: 'hidden',
            border: `1px solid ${app.codeDark ? CODE_THEME.dark.border : CODE_THEME.light.border}`,
            background: app.codeDark ? CODE_THEME.dark.bg : CODE_THEME.light.bg,
          }}
        >
          <CodeBlock
            langs={langs}
            style={{ border: 'none', borderRadius: 0 }}
            maxHeight={340}
            headerRight={
              <span className="om-mono" style={{ fontSize: 11, color: app.codeDark ? CODE_THEME.dark.cm : CODE_THEME.light.cm }}>
                Request
              </span>
            }
          />
          <TryIt
            ep={ep}
            bodyDraft={bodyDraft}
            setBodyDraft={setBodyDraft}
            concretePath={concretePath}
            result={result}
            setResult={setResult}
            onLiveRequest={handleLiveRequest}
          />
        </div>
        {/* Collapsible example response — auto-collapses when a live request is made */}
        <div style={{ marginTop: 14 }}>
          <button
            onClick={() => setExampleCollapsed((c) => !c)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginBottom: exampleCollapsed ? 0 : 8,
            }}
          >
            <Icon name={exampleCollapsed ? 'chevron-right' : 'chevron-down'} size={12} color="var(--text-secondary)" />
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}
            >
              Example response · {responseStatusLabel}
            </span>
          </button>
          {!exampleCollapsed && <CodeBlock dark={app.codeDark} code={jsonStr(ep.response)} maxHeight={300} />}
        </div>
      </div>
    </div>
  );
}

export { RefNav, ParamTable, TryIt, EndpointDoc };
export type { EndpointData };
